/**
 * Saving (1.AC-2 deliverable 1, pure): what a save sends, how the answer is adopted and how 1.AC-1's
 * refusals read. The server keeps its own `source`, so an update carries the stored `data.source`.
 */
import {
  type ActorSheetV1,
  ApiError,
  type SheetCreateV1,
  type SheetUpdateV1,
} from '@codex/contracts';
import type { StatBlockV1 } from '@codex/dnd5e/schema';
import type { FormState } from './draft.js';

export type SavePlan =
  | { readonly kind: 'create'; readonly body: SheetCreateV1 }
  | { readonly kind: 'update'; readonly body: SheetUpdateV1 }
  | { readonly kind: 'none' };

/** Structural equality of JSON values (key order does not matter). */
export function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every(
    (key) =>
      Object.hasOwn(b, key) &&
      sameJson((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
}

/** The form state a saved sheet stands for. */
export function stateFromSheet(sheet: ActorSheetV1): FormState {
  return {
    value: sheet.data,
    label: sheet.label ?? '',
    isPrimary: sheet.isPrimary,
    filledBy: 'form',
  };
}

function labelOf(state: FormState): string | null {
  const trimmed = state.label.trim();
  return trimmed === '' ? null : trimmed;
}

/** `create` for a draft with no saved sheet, `update` with only what changed, else `none`. */
export function savePlan(state: FormState, saved?: ActorSheetV1 | null): SavePlan {
  if (!saved) {
    const label = labelOf(state);
    const body: SheetCreateV1 = {
      ...(label === null ? {} : { label }),
      isPrimary: state.isPrimary,
      data: state.value,
      source: state.filledBy === 'paste' ? 'paste' : 'form',
    };
    return { kind: 'create', body };
  }
  const body: { -readonly [K in keyof SheetUpdateV1]: SheetUpdateV1[K] } = {
    baseVersion: saved.version,
  };
  let changed = false;
  const label = labelOf(state);
  if (label !== saved.label) {
    body.label = label;
    changed = true;
  }
  if (state.isPrimary !== saved.isPrimary) {
    body.isPrimary = state.isPrimary;
    changed = true;
  }
  const data: StatBlockV1 = { ...state.value, source: saved.data.source };
  if (!sameJson(data, saved.data)) {
    body.data = data;
    changed = true;
  }
  return changed ? { kind: 'update', body } : { kind: 'none' };
}

/** After a successful create or update the saved sheet and the draft are the answer's sheet. */
export function adoptSaved(
  state: FormState,
  answer: ActorSheetV1,
): { state: FormState; saved: ActorSheetV1 } {
  return { state: { ...stateFromSheet(answer), filledBy: state.filledBy }, saved: answer };
}

/** Whether the draft differs from the state it started from (a saved sheet, or the new form). */
export function formDirty(state: FormState, baseline: FormState): boolean {
  return (
    state.label !== baseline.label ||
    state.isPrimary !== baseline.isPrimary ||
    !sameJson(state.value, baseline.value)
  );
}

export type SaveError =
  | { readonly kind: 'conflict' }
  | { readonly kind: 'message'; readonly text: string };

const ISSUE_TEXT: Readonly<Record<string, string>> = {
  type_without_sheet: "This page's type doesn't have stat blocks.",
  rules_2024: 'Only 2014 stat blocks can be saved.',
  primary_required: 'A page keeps one primary stat block.',
};

const GENERIC = "Couldn't save the stat block.";

/** 1.AC-1's answers as the form shows them. */
export function errorMessage(error: unknown): SaveError {
  if (!(error instanceof ApiError)) return { kind: 'message', text: GENERIC };
  if (error.status === 409 && error.code === 'row_conflict') return { kind: 'conflict' };
  if (error.status === 409 && error.code === 'document_trashed') {
    return {
      kind: 'message',
      text: 'This page is in the trash. Restore it to edit its stat block.',
    };
  }
  if (error.status === 422 && error.code === 'invalid_body') {
    const details = error.body.details;
    const issue =
      typeof details === 'object' && details !== null
        ? (details as { issue?: unknown }).issue
        : undefined;
    const text = typeof issue === 'string' ? ISSUE_TEXT[issue] : undefined;
    return { kind: 'message', text: text ?? GENERIC };
  }
  if (error.status === 404) return { kind: 'message', text: 'This page no longer exists.' };
  return { kind: 'message', text: GENERIC };
}
